import Color from 'color';

import {
  PALETTE_TONAL_OFFSETS,
  SIZING_NAMES,
  TOKEN_PALETTE_REF_PREFIX,
  TOKEN_TYPESCALE_PREFIX,
} from './GlobalStylesConstants.js';
import { ColorParam, PaletteKeyColorNames } from './GlobalStylesTypes.js';

export const tonalColor = (
  color: ColorParam,
  tonal: number,
  format: 'hsl' | 'hex' | 'rgb' = 'hsl'
) => {
  const baseColor = Color(color).hsl().array();

  if (tonal < 0 || tonal > 100) {
    throw new Error(`Invalid tonal value: ${tonal}`);
  }

  return (Color([baseColor[0], baseColor[1], tonal], 'hsl') as any)[format]().toString();
};

export const generateTonalOffsets = (
  color: ColorParam,
  tonalOffsets = PALETTE_TONAL_OFFSETS as readonly number[]
) => {
  const palette = [];
  for (let i = 0; i < tonalOffsets.length; i++) {
    palette.push({
      color: tonalColor(color, tonalOffsets[i]),
      offset: tonalOffsets[i],
    });
  }

  return palette.reduce((acc, { color, offset }) => {
    acc[offset] = color;
    return acc;
  }, {} as { [key: string]: string });
};

export const printToken = (prefix: string, name: string) => {
  return `${prefix}-${name}`;
};

export const printTokenValue = (prefix: string, name: string, value: string) => {
  return `${printToken(prefix, name)}: ${value};`;
};

export const printTokenRelativeColor = (tonalOffset: number, token: string) =>
  `hsl(from var(${token}) h s ${tonalOffset}%)`;

export const printRefPaletteColorTonalOffsetTokens = (
  colorName: string,
  tonalOffsets = PALETTE_TONAL_OFFSETS as readonly number[],
  formatter: (tonalOffset: number, token: string) => string,
  prefix = TOKEN_PALETTE_REF_PREFIX
) => {
  return tonalOffsets
    .map((tonalOffset) =>
      printTokenValue(
        prefix,
        `${colorName}${tonalOffset}`,
        formatter(tonalOffset, printToken(prefix, colorName))
      )
    )
    .join('\n');
};

export const printRefPaletteColorTonalOffsetTokensLegacy = (
  colorName: string,
  colorValue: string,
  tonalOffsets = PALETTE_TONAL_OFFSETS as readonly number[],
  prefix = TOKEN_PALETTE_REF_PREFIX
) => {
  return tonalOffsets
    .map((tonalOffset) =>
      printTokenValue(prefix, `${colorName}${tonalOffset}`, tonalColor(colorValue, tonalOffset))
    )
    .join('\n');
};

export const printRefPaletteTokens = (
  colorMapping: Record<string, string | [string, number[]]>,
  relative = false,
  prefix = TOKEN_PALETTE_REF_PREFIX
) => {
  return Object.entries(colorMapping)
    .flatMap(([colorName, colorValue]) => {
      const [$colorValue, $tonalOffsets] =
        typeof colorValue === 'string' ? [colorValue] : colorValue;

      const formatter = relative
        ? printTokenRelativeColor
        : (tonalOffset: number) => tonalColor(colorValue, tonalOffset, 'hsl');

      return [
        printTokenValue(prefix, colorName, new Color($colorValue).hsl().toString()),
        printRefPaletteColorTonalOffsetTokens(colorName, $tonalOffsets, formatter, prefix),
      ];
    })
    .join('\n');
};

export const printSystemColorTokens = (
  schemaName: string,
  schema: Record<PaletteKeyColorNames, string>
) => {
  return Object.keys(schema)
    .map(
      (key) =>
        `--spz-sys-color-${key}-${schemaName}: var(--spz-ref-palette-${
          schema[key as PaletteKeyColorNames]
        });`
    )
    .join('\n');
};

export const printTheme = (themeSchema: string, colorRoles: readonly string[]) =>
  colorRoles.reduce(
    (acc, colorRole) =>
      `${acc}\n--spz-sys-color-${colorRole}: var(--spz-sys-color-${colorRole}-${themeSchema});`,
    ''
  );

export const printTypescaleTokens = (
  mapping: Record<string, Record<string, string | string[]>>,
  prefix = TOKEN_TYPESCALE_PREFIX
) => {
  return Object.entries(mapping)
    .flatMap(([variantName, variantDef]) => {
      return SIZING_NAMES.flatMap((sizeName, sizeIndex) => {
        return Object.entries(variantDef).map(([propName, propVal]) => {
          const val = Array.isArray(propVal) ? propVal[sizeIndex] : propVal;
          return `${prefix}-${variantName}-${sizeName}-${propName}: ${val};`;
        });
      });
    })
    .join('\n');
};

export const printFontFace = (
  family: string,
  url: string,
  weight: number,
  style: string,
  display: string = 'swap'
) => {
  return `
    @font-face {
      font-display: ${display};
      font-family: '${family}';
      font-style: ${style};
      font-weight: ${weight};
      src: url('${url}');
    }
  `;
};

export const printFontFaces = (
  fontFaces: Record<
    string,
    {
      url: string;
      weight: number;
      style: string;
      display?: string;
    }[]
  >
) =>
  Object.entries(fontFaces)
    .flatMap(([family, faces]) =>
      faces.map((face) => printFontFace(family, face.url, face.weight, face.style, face.display))
    )
    .join('\n');
